<template>
  <div class="progressive-map no-select">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlns:xlink="http://www.w3.org/1999/xlink"
      viewBox="0 0 2400 1700"
      class="interactive-map"
      ref="interactiveMap"
    >
      <defs>
        <image
          id="image-2"
          width="86"
          height="55"
          :xlink:href="`${bucketUrl}/1/subtest-backgrounds/progressive-map/sheep.png?v=${assetsVersion}`"
        />
        <image
          id="image-4"
          width="310"
          height="317"
          :xlink:href="`${bucketUrl}/1/subtest-backgrounds/progressive-map/ThunderGem.png?v=${assetsVersion}`"
        />
      </defs>
      <g id="flip-z-area" ref="flipZones">
        <rect id="area-3" class="cls-6" x="793.8" y="861.69" width="246.48" height="81.03" />
        <rect id="area-2-1" class="cls-6" x="1337.57" y="946.67" width="247.03" height="144.26" />
        <rect id="area-1" class="cls-6" x="651.45" y="1176.15" width="217.06" height="81.03" />
        <rect id="area-2-2" class="cls-6" x="685.98" y="974.45" width="250.53" height="90.24" />
      </g>
      <g id="background" ref="backgroundElement">
        <image
          width="2400"
          height="1700"
          class="map-loading-transition"
          :xlink:href="`${bucketUrl}/1/subtest-backgrounds/progressive-map/ScreenerProgressiveBackground-low.png?v=${assetsVersion}`"
          :style="{ opacity: mainMapLoaded || !mapPlaceholderLoaded ? 0 : 1 }"
          @load="mapPlaceholderLoaded = true"
        />
        <image
          width="2400"
          height="1700"
          :xlink:href="`${bucketUrl}/1/subtest-backgrounds/progressive-map/ScreenerProgressiveBackground.png?v=${assetsVersion}`"
          :srcset="`${bucketUrl}/1/subtest-backgrounds/progressive-map/ScreenerProgressiveBackground-900x638.png?v=${assetsVersion} 900w,
                    ${bucketUrl}/1/subtest-backgrounds/progressive-map/ScreenerProgressiveBackground-1400x992.png?v=${assetsVersion} 1400w,
                    ${bucketUrl}/1/subtest-backgrounds/progressive-map/ScreenerProgressiveBackground-1800x1275.png?v=${assetsVersion} 1800w,
                    ${bucketUrl}/1/subtest-backgrounds/progressive-map/ScreenerProgressiveBackground-2400x1700.png?v=${assetsVersion} 2400w`"
          :sizes="defaultSizes"
          class="map-loading-transition"
          :style="{ opacity: mainMapLoaded ? 1 : 0 }"
          @load="handleHighResMainBGLoad"
        />
      </g>
      <g
        id="sheep-spots"
        class="map-loading-transition"
        :style="{ opacity: allImagesLoaded ? 1 : 0 }"
      >
        <use
          v-for="i in 5"
          :key="`sheep-${i}`"
          :id="`sheep-${i}`"
          :transform="getSheepTransform(i)"
          xlink:href="#image-2"
          :class="{ show: shouldShowSheep(i) }"
        />
      </g>

      <g
        id="thunder-gems"
        class="map-loading-transition"
        :style="{ opacity: allImagesLoaded ? 1 : 0 }"
      >
        <use
          v-for="i in 5"
          :key="`ThunderGem-${i}`"
          :id="`ThunderGem-${i}`"
          :transform="getGemTransform(i)"
          xlink:href="#image-4"
          :style="{ display: shouldGemBeHidden(i) ? 'none' : 'inherit' }"
          :class="{ 'fall-animation': fallingThundergem === i }"
        />
        <image
          id="ThunderGemShip"
          width="195"
          height="141"
          transform="translate(621.38 660.88) scale(.25)"
          :xlink:href="`${bucketUrl}/1/subtest-backgrounds/progressive-map/ThunderGemShip.png?v=${assetsVersion}`"
        />
      </g>
      <g id="viewport">
        <rect class="cls-6" x="484.46" y="341.83" width="1423.64" height="1065.78" />
      </g>
      <g id="avatar-path" ref="avatarPathRef">
        <path
          id="path-5-6"
          class="cls-5"
          d="M1551.85,927.27c-1.5-19.32-17.06-34.78-161.03-67.85-209.46-48.12-327.69-43.38-428.23-55.04"
        />
        <path
          id="path-4-5"
          class="cls-5"
          d="M630.92,955.75c.1-1.22.43-2.41,1-3.57,18.16-37.18,112.79-30.77,205.48-31.56,36.41-.31,202.75-3.57,257.43,5.98,72.38,12.64,92.67,19.41,197.91,39.76,185.21,35.82,259.11-15.8,259.26-33.17.02-2.03,0-4-.14-5.93"
        />
        <path
          id="path-3-4"
          class="cls-5"
          d="M1704.26,1132.18c-20.45-42.47-62.29-58.98-169.38-73.34-79.82-10.7-197.35-.76-353.85-3.93-91.12-1.85-183.25-5.47-258.12-13.75-183.31-20.29-294.12-58.71-291.99-85.41"
        />
        <path
          id="path-2-3"
          class="cls-5"
          d="M932.09,1220.33c284.04,3.02,559.84-10.01,691.77-29.93,66.46-10.04,91.57-35.03,80.4-58.22"
        />
        <path id="path-1-2" class="cls-5" d="M486.14,1190.9s69.23,25.43,445.94,29.43" />
      </g>
    </svg>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlns:xlink="http://www.w3.org/1999/xlink"
      viewBox="0 0 2400 1700"
      class="chest-layer map-loading-transition"
      :class="{
        'flip-zone': inProgressiveMapFlipZone
      }"
      :style="{ opacity: allImagesLoaded ? 1 : 0 }"
    >
      <defs>
        <image
          id="chest-open"
          width="370"
          height="449"
          :xlink:href="`${bucketUrl}/1/subtest-backgrounds/progressive-map/OpenStatic.png?v=${assetsVersion}`"
        />
        <image
          id="chest-closed"
          width="370"
          height="335"
          :xlink:href="`${bucketUrl}/1/subtest-backgrounds/progressive-map/ClosedStatic.png?v=${assetsVersion}`"
        />
      </defs>
      <g id="chest-spots">
        <g id="chest-5" :class="shouldChestBeOpen(5)">
          <ellipse class="cls-4" cx="898.64" cy="845" rx="51.85" ry="19.25" />
          <ellipse class="cls-1" cx="899.53" cy="842.44" rx="51.85" ry="19.25" />
          <use
            id="closed-5"
            transform="translate(870.31 793.98) scale(.16)"
            xlink:href="#chest-closed"
          />
          <use
            id="open-5"
            transform="translate(870.31 775.84) scale(.16)"
            xlink:href="#chest-open"
          />
        </g>
        <g id="chest-3" :class="shouldChestBeOpen(3)">
          <ellipse class="cls-12" cx="759.98" cy="962.87" rx="51.85" ry="19.25" />
          <ellipse class="cls-3" cx="760.87" cy="960.31" rx="51.85" ry="19.25" />
          <use
            id="closed-3"
            transform="translate(731.33 909.72) scale(.16)"
            xlink:href="#chest-closed"
          />
          <use
            id="open-3"
            transform="translate(731.33 891.58) scale(.16)"
            xlink:href="#chest-open"
          />
        </g>
        <g id="chest-1" :class="shouldChestBeOpen(1)">
          <ellipse class="cls-11" cx="1015.85" cy="1298.52" rx="51.85" ry="19.25" />
          <ellipse class="cls-10" cx="1016.74" cy="1295.96" rx="51.85" ry="19.25" />
          <use
            id="closed-1"
            transform="translate(988.32 1245.08) scale(.16)"
            xlink:href="#chest-closed"
          />
          <use
            id="open-1"
            transform="translate(988.32 1226.94) scale(.16)"
            xlink:href="#chest-open"
          />
        </g>
        <g id="chest-2" :class="shouldChestBeOpen(2)">
          <ellipse class="cls-2" cx="1832.28" cy="1137.14" rx="51.85" ry="19.25" />
          <ellipse class="cls-13" cx="1833.17" cy="1134.58" rx="51.85" ry="19.25" />
          <use
            id="closed-2"
            transform="translate(1803.92 1079.71) scale(.16)"
            xlink:href="#chest-closed"
          />
          <use
            id="open-2"
            transform="translate(1803.92 1061.57) scale(.16)"
            xlink:href="#chest-open"
          />
        </g>
        <g id="chest-4" :class="shouldChestBeOpen(4)">
          <ellipse class="cls-9" cx="1446.84" cy="920.16" rx="51.85" ry="19.25" />
          <ellipse class="cls-7" cx="1447.73" cy="917.6" rx="51.85" ry="19.25" />
          <use
            id="closed-4"
            transform="translate(1420.07 867.78) scale(.16)"
            xlink:href="#chest-closed"
          />
          <use
            id="open-4"
            transform="translate(1420.07 849.65) scale(.16)"
            xlink:href="#chest-open"
          />
        </g>
      </g>
    </svg>
    <template v-for="(layer, index) in backgroundLayers" :key="index">
      <div
        class="background-wrapper"
        :class="{
          foreground: currentSubtest?.index === index
        }"
        :style="layerStyle"
      >
        <img
          :srcset="layer.srcset"
          :sizes="defaultSizes"
          :src="layer.src"
          :alt="`Background layer ${index + 1}`"
          class="background-layer map-loading-transition"
          @load="handleHighResLoad(index)"
          @error="retryImageLoad(index)"
          loading="lazy"
          :style="{ opacity: allImagesLoaded ? 1 : 0 }"
        />
      </div>
    </template>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, onBeforeUnmount, nextTick, onMounted } from 'vue'
import { storeToRefs } from 'pinia'

// Types
import type { AvatarWalkingCoordinates, ActiveChestPosition } from '@/customTypes'

// Stores
import { useDataStore } from '@/stores/DataStore'
import { useProgressiveMapStore } from '@/stores/ProgressiveMapStore'

// Helpers
import { useTimeout } from '@/utils/helpers'

import Logger from '@/services/Logger.js'

const logger = new Logger('ProgressiveMapComponent', true)

const emit = defineEmits<{
  (e: 'chest-position-updated', position: ActiveChestPosition): void
  (e: 'map-loaded'): void
}>()

const props = defineProps<{
  rewardContainer: HTMLElement | null
}>()

const { createManagedDebounce } = useTimeout()

const dataStore = useDataStore()
const progressiveMapStore = useProgressiveMapStore()
const { currentSubtest, starPopGameActive } = storeToRefs(dataStore)
const { inProgressiveMapFlipZone } = storeToRefs(progressiveMapStore)
const bucketUrl = _ASSETS_BUCKET_URL_
const assetsVersion = _ASSETS_VERSION_
const defaultSizes =
  '(max-width: 768px) 900px, (max-width: 1024px) 1400px, (max-width: 1920px) 1800px, 2400px'

const avatarPathRef = ref<SVGPathElement | null>(null)
const avatarPathSegments = ref<Record<string, SVGPathElement>>({})

const layerStyle = ref({})
const currentWaypoint = computed(() => (currentSubtest.value?.index ?? 0) + 1)

const backgroundElement = ref<SVGGraphicsElement | null>(null)
const interactiveMap = ref<SVGSVGElement | null>(null)
const flipZones = ref<SVGGraphicsElement | null>(null)
const isMapReady = ref(false)
const mainMapLoaded = ref(false)
const mapPlaceholderLoaded = ref(false)
const fallingThundergem = ref<number | null>(null)
const currentChestLocation = ref<number>()
const backgroundLayersToLoad = ref(5)
const imageLoadRetryCounts = ref(new Map<number, number>())
const imageLoadMaxRetries = 2

// Loading state
const loadedHighResImages = ref(new Set<number>())

const allImagesLoaded = computed(
  () => loadedHighResImages.value.size === backgroundLayers.value.length && mainMapLoaded.value
)

// Background layers to help track loading status of the layers
const backgroundLayers = computed(() => {
  const layers = []
  for (let i = 0; i < backgroundLayersToLoad.value; i++) {
    layers.push({
      srcset: `${bucketUrl}/1/subtest-backgrounds/progressive-map/ScreenerProgressive${i}-${i + 1}-900x638.png?v=${assetsVersion} 900w,
               ${bucketUrl}/1/subtest-backgrounds/progressive-map/ScreenerProgressive${i}-${i + 1}-1400x992.png?v=${assetsVersion} 1400w,
               ${bucketUrl}/1/subtest-backgrounds/progressive-map/ScreenerProgressive${i}-${i + 1}-1800x1275.png?v=${assetsVersion} 1800w,
               ${bucketUrl}/1/subtest-backgrounds/progressive-map/ScreenerProgressive${i}-${i + 1}-2400x1700.png?v=${assetsVersion} 2400w`,
      src: `${bucketUrl}/1/subtest-backgrounds/progressive-map/ScreenerProgressive${i}-${i + 1}-900x638.png?v=${assetsVersion}`
    })
  }
  return layers
})

// NOTE: Lifecycle
onMounted(() => {
  currentChestLocation.value = (currentSubtest.value?.index || 0) + 1
})

onBeforeUnmount(() => {
  progressiveMapStore.$reset()
  window.removeEventListener('resize', resizeDebounce)
})

// NOTE: Methods

//Loading functions to watch until all images are loaded before initializing the map
const handleHighResMainBGLoad = () => {
  mainMapLoaded.value = true
  checkIfMapReady()
}

const handleHighResLoad = (index: number) => {
  loadedHighResImages.value.add(index)
  checkIfMapReady()
}

// Retry loading the image if it fails
const retryImageLoad = (index: number) => {
  const retries = imageLoadRetryCounts.value.get(index) || 0
  if (retries < imageLoadMaxRetries) {
    imageLoadRetryCounts.value.set(index, retries + 1)
    backgroundLayers.value[index].src = `${backgroundLayers.value[index].src}?retry=${Date.now()}`
  } else {
    logger.warn(`Failed to load image at index ${index} after ${imageLoadMaxRetries} retries.`)
  }
}

const checkIfMapReady = () => {
  if (
    backgroundLayers.value.length === backgroundLayersToLoad.value &&
    loadedHighResImages.value.size === backgroundLayers.value.length &&
    mainMapLoaded.value
  ) {
    initializeMap()
  }
}

// Map initialization only once images have loaded
const initializeMap = async () => {
  if (!avatarPathRef.value) {
    logger.error('progressive map svg element not found')
    return
  }
  Array.from(avatarPathRef.value.querySelectorAll('path')).forEach((path: SVGPathElement) => {
    const id = path.id
    if (id) {
      avatarPathSegments.value[id] = path
    }
  })

  progressiveMapStore.initializeMapComponents(
    interactiveMap.value,
    avatarPathSegments.value,
    flipZones.value
  )

  window.addEventListener('resize', resizeDebounce)

  handleResize()
  // Call to start the loading of reward chest animations after calculating the path
  emit('map-loaded')
  triggerGemFall((currentSubtest.value?.index ?? 0) + 1)
  isMapReady.value = true
}

const handleResize = () => {
  if (starPopGameActive.value) {
    window.removeEventListener('resize', resizeDebounce)
    return
  }

  if (!interactiveMap.value) {
    logger.error('progressive map svg element not found')
    return
  }

  if (interactiveMap.value && avatarPathRef.value) {
    checkActiveChestPosition()
    updateLayerStyle()
  }
}

// A debounced version of the resize handler to prevent excessive calls when the page is resized
const resizeDebounce = createManagedDebounce(() => {
  handleResize()
}, 100)

// Gets the path between the current subtest waypoint and the next
const getPathForAvatar = async (): Promise<AvatarWalkingCoordinates | null> => {
  // Wait for map to be ready once the path is returned the avatar will walk it loaded / not
  if (!isMapReady.value) {
    logger.warn('Map not ready, waiting for initialization')

    // Wait for map to be ready with timeout
    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Map initialization timeout'))
      }, 10000)

      const checkReady = () => {
        if (isMapReady.value) {
          clearTimeout(timeout)
          resolve(true)
        } else {
          setTimeout(checkReady, 100)
        }
      }
      checkReady()
    }).catch((error) => {
      logger.error('Map initialization failed:', error)
      return null
    })
  }

  const currentIndex = Math.max(1, currentWaypoint.value)
  const pathPoints = progressiveMapStore.getAvatarPathSegment(currentIndex, currentIndex + 1)

  if (!pathPoints) {
    logger.warn('No path segment found for waypoints', currentIndex, currentIndex + 1)
    return null
  }

  return {
    startX: pathPoints[0].x,
    startY: pathPoints[0].y,
    targetX: pathPoints[pathPoints.length - 1].x,
    targetY: pathPoints[pathPoints.length - 1].y,
    pathPoints: pathPoints
  }
}

// Updates the current active chest position so the animation is aligned with the chest position
const checkActiveChestPosition = async () => {
  await nextTick()
  const position = getActiveChestPosition() as ActiveChestPosition
  emit('chest-position-updated', position)
}

// Gets the active chest position for the current waypoint used to align the chest animation to
const getActiveChestPosition = (): ActiveChestPosition | null => {
  const activeChest = document.querySelector(`#chest-${currentWaypoint.value}.active`)
  if (!activeChest || !props.rewardContainer) {
    logger.warn('No active chest found')
    return null
  }

  const chestBBox = activeChest.getBoundingClientRect()

  const containerBBox = props.rewardContainer.getBoundingClientRect()

  // Calculate active chest position relative to the map container
  return {
    x: chestBBox.left - containerBBox.left + chestBBox.width / 2,
    y: chestBBox.top - containerBBox.top + chestBBox.height / 2,
    width: chestBBox.width * 2
  }
}

// Scales the background layers to match the size of the background within the SVG due to the way it handles preserving it's aspect ratio
const updateLayerStyle = () => {
  const dimensions = getBackgroundDimensions()
  layerStyle.value = {
    width: `${dimensions.width}px`,
    height: `${dimensions.height}px`
  }
}

// Due to the SVG preserving a aspect ratio we need to get the correct sizing for the background to make sure the layers are aligned
const getBackgroundDimensions = () => {
  if (!backgroundElement.value || !interactiveMap.value) {
    return { width: 0, height: 0 }
  }

  const bbox = backgroundElement.value.getBBox()
  const svgRect = interactiveMap.value.getBoundingClientRect()
  const { width: viewBoxWidth, height: viewBoxHeight } = interactiveMap.value.viewBox.baseVal

  // Detect orientation
  const isLandscape = window.innerWidth > window.innerHeight

  let scaleX, scaleY

  // Handle scaling based on orientation
  if (isLandscape) {
    const svgAspectRatio = viewBoxWidth / viewBoxHeight
    const renderedAspectRatio = svgRect.width / svgRect.height

    if (renderedAspectRatio > svgAspectRatio) {
      scaleY = svgRect.height / viewBoxHeight
      scaleX = scaleY
    } else {
      scaleX = svgRect.width / viewBoxWidth
      scaleY = scaleX
    }
  } else {
    const svgAspectRatio = viewBoxHeight / viewBoxWidth
    const renderedAspectRatio = svgRect.height / svgRect.width

    if (renderedAspectRatio > svgAspectRatio) {
      scaleY = svgRect.width / viewBoxHeight
      scaleX = scaleY
    } else {
      scaleX = svgRect.height / viewBoxWidth
      scaleY = scaleX
    }
  }

  return {
    width: bbox.width * scaleX,
    height: bbox.height * scaleY
  }
}

// Different sheep locations
const getSheepTransform = (sheepNumber: number) => {
  const transforms = {
    1: 'translate(1543.7 1235.91) rotate(-180) scale(1 -1)',
    2: 'translate(1493.51 960.05)',
    3: 'translate(1149.19 1106.55)',
    4: 'translate(1814.01 1015.25) rotate(-180) scale(1 -1)',
    5: 'translate(1389.59 1324.14)'
  }

  return transforms[sheepNumber as keyof typeof transforms] || ''
}

// Thunder gem locations may have to change based on the map if changed
const getGemPositions = (gemNumber: number) => {
  switch (gemNumber) {
    case 1:
      return { x: 1128.95, y: 1240.55 }
    case 2:
      return { x: 1809.76, y: 1251.29 }
    case 3:
      return { x: 518.41, y: 935.58 }
    case 4:
      return { x: 1566.39, y: 793.98 }
    case 5:
      return { x: 852.49, y: 697.92 }
    default:
      return { x: 0, y: 0 }
  }
}

const getGemTransform = (gemNumber: number): string => {
  const pos = getGemPositions(gemNumber)

  if (!pos) return ''

  // Return the complete transform string with translation and scale
  return `translate(${pos.x} ${pos.y}) scale(.25)`
}

// If the thunder gem has been defeated in the area drop them off the map
const triggerGemFall = (gemNumber: number) => {
  const gem = document.querySelector(`#ThunderGem-${gemNumber}`) as HTMLElement | null
  const pos = getGemPositions(gemNumber)

  if (gem) {
    if (pos) {
      gem.style.setProperty('--gem-x', `${pos.x}px`)
      gem.style.setProperty('--gem-y', `${pos.y}px`)
      fallingThundergem.value = gemNumber
    }
  }
}

const shouldGemBeHidden = (gemNumber: number) => {
  return gemNumber <= (currentChestLocation.value || 1) - 1
}

const shouldShowSheep = (sheepNumber: number) => {
  return sheepNumber === currentChestLocation.value
}

const shouldChestBeOpen = (chestNumber: number) => {
  return chestNumber === (currentChestLocation.value || 1)
    ? 'active'
    : (currentChestLocation.value || 0) > chestNumber
      ? 'open'
      : 'closed'
}

defineExpose({ getPathForAvatar, checkActiveChestPosition })
</script>
<style scoped lang="scss">
.progressive-map {
  position: absolute;
  overscroll-behavior: none;
  left: 0;
  top: 0;

  .cls-1 {
    fill: #76c6db;
  }
  .cls-2 {
    fill: #4a8ed1;
  }
  .cls-3 {
    fill: #db7b80;
  }
  .cls-4 {
    fill: #5fb8d1;
  }
  .cls-5 {
    stroke: #f0f;
  }
  .cls-5,
  .cls-6 {
    fill: none;
    stroke-miterlimit: 10;
  }
  .cls-7 {
    fill: #db9bd3;
  }
  .cls-8 {
    fill: #014900;
  }
  .cls-6 {
    stroke: red;
    stroke-width: 10px;
  }
  .cls-9 {
    fill: #d163c1;
  }
  .cls-10 {
    fill: #aba4db;
  }
  .cls-11 {
    fill: #988fd1;
  }
  .cls-12 {
    fill: #d14c50;
  }
  .cls-13 {
    fill: #95b9db;
  }

  .st0,
  .st1 {
    fill: #ffee60;
  }

  .map-loading-transition {
    transition: 'opacity 0.5s ease';
  }

  .interactive-map,
  .chest-layer {
    position: fixed;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);

    // Uses the sizing from viewport rect within the Maps SVG (1423x1065) as our reference for scaling to frame the center of the map in the screen
    width: calc(var(--full-vw) * 2400 / 1423);
    height: calc(var(--full-vh) * 1700 / 1065);
    min-width: var(--full-vw);
    min-height: var(--full-vh);
  }

  .chest-layer {
    z-index: 1015;
    pointer-events: none;
    &.flip-zone {
      z-index: 1009 !important;
    }
  }

  // background layers are assigned a sizing from from the SVG to maintain the correct aspect ratio
  .background-wrapper {
    position: fixed;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
    overflow: hidden;

    &.foreground {
      z-index: 1015;
    }
  }

  #avatar-path,
  #viewport,
  #flip-z-area {
    opacity: 0;
    z-index: 1;
  }

  .background-layer {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    transition: opacity 0.5s ease;

    &.visible {
      opacity: 1;
    }
  }

  #sheep-spots {
    use {
      transition: opacity 0.3s ease;
      opacity: 0;
      &.show {
        opacity: 1;
      }
    }
  }

  #chest-spots {
    g {
      &.active {
        [id^='open'],
        [id^='closed'] {
          display: none;
        }
      }

      &.closed {
        [id^='open'] {
          display: none;
        }
        [id^='closed'] {
          display: block;
        }
      }

      &.open {
        [id^='closed'] {
          display: none;
        }
        [id^='open'] {
          display: block;
        }
      }
    }
  }

  .background-wrapper,
  .background-layer {
    will-change: opacity;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
  }

  // Thunder gem animation off the screen
  .fall-animation {
    animation: fallOff 3.8s cubic-bezier(0.55, 0.085, 0.68, 1.53) forwards;
  }

  @keyframes fallOff {
    0%,
    60% {
      transform: translate(var(--gem-x), var(--gem-y)) scale(0.25) rotate(0deg);
      opacity: 1;
    }
    80% {
      transform: translate(var(--gem-x), var(--gem-y)) scale(0.25) rotate(-10deg);
    }
    100% {
      transform: translate(var(--gem-x), calc(var(--gem-y) + 1000px)) scale(0.25) rotate(30deg);
      opacity: 0;
    }
  }
}
</style>
